import { ComponentProps, ExtractSlotNames } from "@/types/helpers"
import { Component, ExtractPropTypes } from "vue"
import { RouteLocationRaw } from "vue-router"
import {
  inputElementTypeMap,
  displayElementTypeMap,
} from "../schema/elementTypes"

type ComponentModelValueType<C extends Component> =
  ComponentProps<C>["modelValue"]

type DisplayElementTypes = typeof displayElementTypeMap
type InputElementTypes = typeof inputElementTypeMap
type InputElementKeys = keyof InputElementTypes

export type ConditionalValue = string | boolean | undefined

/**
 * For each component in DisplayElementTypes produce an Element type, with a set key, e.g.
 * Element<'alert', DisplayElementTypes> | Element<'info', DisplayElementTypes>
 */
type AllowedDisplayElement<
  TElementMap extends Record<string, Component> = DisplayElementTypes,
  TElementKeys extends keyof TElementMap = keyof TElementMap,
> = TElementKeys extends keyof TElementMap
  ? Element<TElementKeys, TElementMap>
  : never

/**
 * Produce a set of InputElement from InputElementKeys config.
 * The 'key' and 'is' are constrained to ensure that only input components emitting
 * modelValue matching type of flow config input type are accepted.
 * For example { is: 'datePicker', key: 'firstName' } should not be accepted.
 */
export type AllowedInputElement<
  TFlowInputs extends object | void = void,
  TFlowInputsTypes extends ExtractPropTypes<TFlowInputs> =
    ExtractPropTypes<TFlowInputs>,
> = {
  [K in InputElementKeys]: InputElement<
    K,
    TFlowInputs extends void ? string : keyof TFlowInputsTypes
  >
}[InputElementKeys]

export const isRawInputElement = (
  element: ElementRaw,
): element is AllowedInputElement =>
  (<AllowedInputElement>element).key !== undefined

export type ElementRaw<FlowInputs extends object | void = void> =
  AllowedInputElement<FlowInputs> | AllowedDisplayElement

type ActionConditionIfs = Array<{ if: ConditionalValue; step: string }>

export type ActionConditions =
  ActionConditionIfs | [...ActionConditionIfs, { step: string }]

type ComponentSlots<T> = {
  [key in ExtractSlotNames<T>]?: string
}

// Generic Element with props constrained based on its component (value of 'is')
type Element<K extends keyof T, T extends Record<string, Component>> = {
  /**
   * Component name
   */
  is: K
  /**
   * Props object passed to the component
   * Type needs to match prop type defined in Vue component
   */
  props?: ComponentProps<T[K]>
  /**
   * Slot objects passed to the component
   */
  slots?: ComponentSlots<K>
  /**
   * Conditionals controlling element visibility
   */
  showIf?: ConditionalValue
  showIfNot?: ConditionalValue
  showBelowActions?: ConditionalValue
}

// Generic InputElement with 'value' constrained based to modelValue of its component
type InputElement<C extends InputElementKeys, K> = Element<
  C,
  InputElementTypes
> & {
  /**
   * Input key, i.e. the name under which the input value should be saved under
   */
  key: K
  /**
   * Initial input value (modelValue) of the element, used for pre-populating fields
   */
  value?: Partial<ComponentModelValueType<InputElementTypes[C]>>
  /**
   * Flag denoting that user can go to the next step without filling the field
   */
  optional?: boolean
}
export interface IActionRaw {
  buttonLabel?: string
  buttonType?: string
  /**
   * Condition defining if the action button is disabled
   * Can be used for defining a custom validation function, especially useful when validation
   * needs to check multiple input fields or getters from outside the form
   */
  disabled?: ConditionalValue
  /**
   * ID of the step to go to after the action is submitted
   */
  nextId?: string
  /**
   * Array of conditions ('if') linked to Step IDs ('step'), used to define conditional navigation,
   * I.e. if condition A, go to step X, else go to step Y.
   *
   * Only applicable if 'nextId' is not defined
   * Conditions are checked sequentially and the first matching one, will trigger navigation.
   * Last condition, is the default one, if no match was found, thus it does not need an 'if' condition.
   */
  nextIf?: ActionConditions
  /**
   * Conditional to hide the action button
   */
  showIf?: ConditionalValue
  showIfNot?: ConditionalValue
  /**
   * Name of an action to commit all input values collected so far into
   */
  saveTo?: string
  /**
   * Name of the route used to push on action click
   */
  to?: string | RouteLocationRaw
}

export interface IStepRaw<FlowInputTypes extends object | void = void> {
  id: string
  title?: string
  description?: string
  descriptionHtml?: string[]
  hideHelpText?: boolean
  elements?: ElementRaw<FlowInputTypes>[]
  actions?: IActionRaw[]
}

export type IFlowRaw<FlowInputs extends object | void = void> = {
  steps: IStepRaw<FlowInputs>[]
}
